import React, { useState, useEffect,useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Task from '../utils/TaskMaster';
import { useNavigate } from 'react-router-dom';
import TaskSelectionDialog from './TaskSelectionDialog';
import { fetchAllTasks } from '../utils/api';
import TaskInputForm from './taskInputForm'; // ← new import
import { LoadingIndicator } from './assets';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Dialog, DialogTitle, DialogContent, Button,Box,IconButton, Typography, useTheme } from '@mui/material'
import { Delete,Edit,ContentCopy,AddBox, DragIndicator } from '@mui/icons-material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { cascadeDueDate } from '../utils/taskDueDates';

const collectTaskIds = (taskNode) => {
  if (!taskNode) return [];
  return [
    taskNode.id,
    ...(taskNode.subtasks || []).flatMap((subtask) => collectTaskIds(subtask)),
  ];
};

const TaskForm = ({
  onSubmit,
  initialTask = null,
  onSubtaskDelete = null,
  onRootDelete = null,
  inputFields,
  disableTaskFetch = false,
  renderIconButtons,
  submitButtonText = "Save Task",
  idPrefix = "task-editor"
}) => {
  const [task, setTask] = useState(initialTask || new Task({
    id: uuidv4(),
    creation_date: new Date(),
    title: '',
    parent_id: null,
    category: '',
    category_id: null,
    category_color: null,
    complete: null,
    frequency: 0,
    working_time: 0,
    importance: 1,
    due_date: null,
    duration: 0,
    alarm: false,
    ordered: false,
    subtasks: []
  }));

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [breakdownTarget, setBreakdownTarget] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [currentTasks, setCurrentTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [expandedItems, setExpandedItems] = useState([task.id]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragState, setDragState] = useState({
    draggedId: null,
    overId: null,
    position: null,
  });
  const taskRef = useRef(task);
  const deletedIdsRef = useRef(new Set());
  const [lastSelectedItem, setLastSelectedItem] = React.useState(null);
  const initialTaskIdRef = useRef(initialTask?.id ?? null);
  const initialTaskIdsRef = useRef(new Set(collectTaskIds(initialTask)));

  const handleItemSelectionToggle = (event, itemId, isSelected) => {
    if (isSelected) {
      setLastSelectedItem(itemId);
    }
  };
  const theme = useTheme();
  const synthBlue = theme.palette.primary.main;

  const handleClose = () => {
    setSelectedTask(null);
    setDialogMode('add');
  };

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAllTasks();
        // Include the current task in the list of tasks to duplicate from
        setCurrentTasks(data.tasks);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setError('Failed to load available tasks.');
        setIsLoading(false);
      }
    };
    if (!disableTaskFetch) {
      fetchTasks();
    } else {
      setCurrentTasks([task.toJSON()]);
    }
  }, [disableTaskFetch]); // Remove task dependency

  useEffect(() => {
    if (disableTaskFetch) {
      setCurrentTasks([task.toJSON()]);
    }
  }, [disableTaskFetch]); // Remove task dependency to prevent flashing

  // Keep currentTasks updated with the task being edited
  useEffect(() => {
    setCurrentTasks(prev => {
      const filtered = prev.filter(t => t.id !== task.id);
      return [...filtered, task.toJSON()];
    });
    // Ensure root task is expanded
    setExpandedItems(prev => (prev.includes(task.id) ? prev : [...prev, task.id]));
  }, [task]);

  useEffect(() => {
    taskRef.current = task;
  }, [task]);

  useEffect(() => {
    if (initialTask) {
      const isNewRootTask = initialTaskIdRef.current !== initialTask.id;
      const currentTaskIds = new Set(collectTaskIds(initialTask));
      setTask(initialTask);
      taskRef.current = initialTask;
      setExpandedItems((previouslyExpanded) => {
        if (isNewRootTask) return [initialTask.id];

        // Parent nodes stay expanded after a child is deleted; only removed IDs
        // are pruned from the controlled expansion state.
        return Array.from(new Set([
          initialTask.id,
          ...previouslyExpanded.filter((id) => currentTaskIds.has(id)),
        ]));
      });
      if (isNewRootTask) {
        setDeletedIds([]);
        deletedIdsRef.current = new Set();
        initialTaskIdsRef.current = new Set(collectTaskIds(initialTask));
      }
      initialTaskIdRef.current = initialTask.id;
    }
  }, [initialTask]);

  const collectSubtreeIds = (taskNode) => {
    if (!taskNode) return [];
    return [
      taskNode.id,
      ...taskNode.subtasks.flatMap((subtask) => collectSubtreeIds(subtask)),
    ];
  };

  const handleTaskUpdate = (updatedTask, { cascadeDueDate: shouldCascadeDueDate = false } = {}) => {
    const current = task.getTask(updatedTask.id);
    if (current) {
      Object.assign(current, updatedTask);
      if (shouldCascadeDueDate) {
        cascadeDueDate(current, updatedTask.due_date);
      }
      // Only update state if we're not in disableTaskFetch mode to prevent flashing
      if (!disableTaskFetch) {
        const refreshedTask = new Task(task.toJSON());
        setTask(refreshedTask);
        taskRef.current = refreshedTask;
      }
    }
  };


  const isDescendant = (potentialParent, childId) => {
    return potentialParent.subtasks.some(
      (sub) => sub.id === childId || isDescendant(sub, childId)
    );
  };

  const moveTask = (dragTaskId, dropTaskId, position) => {
    if (!dragTaskId || dragTaskId === dropTaskId || !position) return;
    const dragTask = task.getTask(dragTaskId);
    const dropTask = task.getTask(dropTaskId);
    if (!dragTask || !dropTask) return;

    // The form owns one task tree, so its root must remain the root.
    if (!dragTask.parent_id) return;
    if (isDescendant(dragTask, dropTaskId)) return; // prevent cycles
    if (position !== 'inside' && !dropTask.parent_id) return;

    const dragParent = task.getTask(dragTask.parent_id);
    if (!dragParent) return;

    dragParent.removeSubtask(dragTaskId);

    if (position === 'inside') {
      dropTask.addSubtask(dragTask, 0);
      expandToNode(dropTask.id);
    } else {
      const dropParent = task.getTask(dropTask.parent_id);
      if (!dropParent) return;
      const targetIndex = dropParent.subtasks.findIndex((subtask) => subtask.id === dropTaskId);
      dropParent.addSubtask(dragTask, position === 'before' ? targetIndex : targetIndex + 1);
    }

    task.updateAllSubtasksOrder();
    const updatedTask = new Task(task.toJSON());
    setTask(updatedTask);
    taskRef.current = updatedTask;
  };

  const addSubtask = (parentId) => {
    console.log(task)
    const parentTask = task.getTask(parentId);
    if (parentTask) {
      const newSubtask = {
        id: uuidv4(),
        user_id: parentTask.user_id,
        creation_date: new Date(),
        title: '',
        parent_id: parentId,
        category: parentTask.category,
        category_id: parentTask.category_id,
        category_color: parentTask.category_color,
        complete: null,
        frequency: parentTask.frequency,
        working_time: 0,
        importance: parentTask.importance,
        due_date: parentTask.due_date,
        duration: 0,
        alarm: false,
        ordered: false,
        subtasks: []
      };
      parentTask.addSubtask(newSubtask,parentTask.subtasks.length);
      task.updateSubtaskOrder();

      expandToNode(newSubtask.id);
      setTask(new Task(task.toJSON()));
      console.log('New subtask added:', task)
      setSelectedTask(newSubtask); // Set the new subtask as selected for editing
      setDialogMode('add');
    }
  };

  const deleteSubtask = (taskId, subtaskId) => {
    const parentTask = task.getTask(taskId);
    if (parentTask) {
      const subtaskToDelete = task.getTask(subtaskId);
      const idsToDelete = collectSubtreeIds(subtaskToDelete);
      if (idsToDelete.length) {
        idsToDelete.forEach((id) => deletedIdsRef.current.add(id));
        setDeletedIds(Array.from(deletedIdsRef.current));
      }
      parentTask.subtasks = parentTask.subtasks.filter((subtask) => subtask.id !== subtaskId);
      task.updateSubtaskOrder();
      const updatedTask = new Task(task.toJSON());
      setTask(updatedTask);
      taskRef.current = updatedTask;
      if (onSubtaskDelete) onSubtaskDelete(taskId, subtaskId, updatedTask);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const latestTask = taskRef.current;
    const currentTaskIds = new Set(collectTaskIds(latestTask));
    const inferredDeletedIds = Array.from(initialTaskIdsRef.current).filter(
      (id) => !currentTaskIds.has(id)
    );
    const mergedDeletedIds = Array.from(
      new Set([...deletedIdsRef.current, ...deletedIds, ...inferredDeletedIds])
    );
    try {
      setIsSubmitting(true);
      await onSubmit(latestTask, { deletedIds: mergedDeletedIds });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDuplicateModal = (targetTaskId) => {
    setDuplicateTarget(targetTaskId);
    setDuplicateError(null);
    setShowDuplicateModal(true);
  };

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateTarget(null);
    setDuplicateError(null);
  };

  const closeBreakdownDialog = () => {
    setBreakdownTarget(null);
  };

  const addManualBreakdown = () => {
    const parentId = breakdownTarget;
    closeBreakdownDialog();
    if (parentId) addSubtask(parentId);
  };

  const duplicateBreakdown = () => {
    const parentId = breakdownTarget;
    closeBreakdownDialog();
    if (parentId) openDuplicateModal(parentId);
  };

  const duplicateSelectedTask = async (id) => {
    if (!id) {
      setDuplicateError('Please select a task to duplicate');
      return;
    }

    try {
      const tasks_to_find = currentTasks.map(t => new Task(t));

      // Step 2: Find the exact task (not just the root) with the matching id
      let taskToDuplicate = null;
      for (const rootTask of tasks_to_find) {
        const foundTask = rootTask.getTask(id);
        if (foundTask) {
          taskToDuplicate = foundTask;
          break;
        }
      }

      if (!taskToDuplicate) {
        throw new Error('Selected task could not be found');
      }

      console.log('Task to duplicate:', taskToDuplicate);
      if (!duplicateTarget) {
        const duplicatedTask = taskToDuplicate.duplicate();
        setTask(duplicatedTask);
      } else {
        console.log(taskToDuplicate)
        const targetTask = task.getTask(duplicateTarget);
        if (targetTask) {
        const duplicatedTask = taskToDuplicate.duplicate(duplicateTarget);
        targetTask.addSubtask(duplicatedTask, targetTask.subtasks.length);
        task.updateSubtaskOrder();
        expandToNode(duplicatedTask.id);
        setTask(new Task(task.toJSON()));
        }
      }

      closeDuplicateModal();
    } catch (error) {
      console.error('Error duplicating task:', error);
      setDuplicateError('Failed to duplicate task.');
    }
  };
  const handleSelect = (event, nodeId) => {
    const selectedTask = task.getTask(nodeId);
    if (selectedTask) {
      setSelectedTask(selectedTask);
      setDialogMode('edit');
    }
  };

  const getAncestorIds = (rootTask, id) => {
    const ancestors = [];
    let current = rootTask.getTask(id);
    while (current && current.parent_id) {
      ancestors.push(current.parent_id);
      current = rootTask.getTask(current.parent_id);
    }
    return ancestors;
  };

  const expandToNode = (id) => {
    const ancestors = getAncestorIds(task, id);
    setExpandedItems(prev => Array.from(new Set([...prev, ...ancestors, id])));
  };

  const resetDragState = () => {
    setDragState({ draggedId: null, overId: null, position: null });
  };

  const handleDragStart = (event, taskId) => {
    // Buttons remain clickable; only the task card itself starts a move.
    if (event.target.closest('button')) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    setDragState({ draggedId: taskId, overId: null, position: null });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragOver = (event, targetId) => {
    event.preventDefault();
    event.stopPropagation();
    if (!dragState.draggedId || targetId === dragState.draggedId) return;

    const draggedTask = task.getTask(dragState.draggedId);
    if (!draggedTask || isDescendant(draggedTask, targetId)) return;

    event.dataTransfer.dropEffect = 'move';
    const { top, height } = event.currentTarget.getBoundingClientRect();
    const pointerPosition = event.clientY - top;
    const position = pointerPosition < height * 0.3
      ? 'before'
      : pointerPosition > height * 0.7
        ? 'after'
        : 'inside';

    setDragState((current) => (
      current.overId === targetId && current.position === position
        ? current
        : { ...current, overId: targetId, position }
    ));
  };

  const handleDrop = (event, targetId) => {
    event.preventDefault();
    event.stopPropagation();
    const draggedId = dragState.draggedId || event.dataTransfer.getData('text/plain');
    moveTask(draggedId, targetId, dragState.position);
    resetDragState();
  };

  const renderRecursiveTasks = (taskObj) => {
    // Subtasks are already sorted in the Task class
    const sortedSubtasks = taskObj.subtasks || [];
    const isDragged = dragState.draggedId === taskObj.id;
    const isDropTarget = dragState.overId === taskObj.id;
    const dropIndicatorSx = {
      height: 42,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      my: 1,
      border: '2px dashed',
      borderColor: 'secondary.main',
      borderRadius: 4,
      bgcolor: 'rgba(255, 45, 149, 0.12)',
      color: 'secondary.main',
      fontWeight: 700,
      fontSize: '0.75rem',
      letterSpacing: 1.5,
      boxShadow: '0 0 14px rgba(255, 45, 149, 0.28)',
    };
    const breakdownRowSx = {
      minHeight: 42,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      my: 1,
      px: 1.5,
      border: '2px dashed',
      borderColor: 'primary.main',
      borderRadius: 4,
      bgcolor: 'rgba(255, 45, 149, 0.12)',
      color: 'secondary.main',
      boxShadow: '0 0 14px rgba(255, 45, 149, 0.28)',
    };
    const defaultButtons = {
      add: (
        <IconButton
          id={`${idPrefix}-add-${taskObj.id}`}
          key="add subtask"
          size="small"
          aria-label={`Add task breakdown to ${taskObj.title || 'task'}`}
          onClick={(e) => { e.stopPropagation(); addSubtask(taskObj.id); }}
          sx={{ p: 0.5 }}
        >
          <AddBox color="green" />
        </IconButton>
      ),
      duplicate: (
        <IconButton
          id={`${idPrefix}-duplicate-${taskObj.id}`}
          key="duplicate"
          size="small"
          aria-label={`Duplicate a task into ${taskObj.title || 'task'}`}
          onClick={(e) => { e.stopPropagation(); openDuplicateModal(taskObj.id); }}
          sx={{ p: 0.5 }}
        >
          <ContentCopy color="orange" />
        </IconButton>
      ),
      edit: (
        <IconButton
          id={`${idPrefix}-edit-${taskObj.id}`}
          key="edit"
          size="small"
          aria-label={`Edit ${taskObj.title || 'task'}`}
          onClick={(e) => { e.stopPropagation(); handleSelect(e, taskObj.id); }}
          sx={{ p: 0.5 }}
        >
          <Edit color="primary" />
        </IconButton>
      ),
      delete: (
        <IconButton
          id={`${idPrefix}-delete-${taskObj.id}`}
          key="delete"
          size="small"
          aria-label={taskObj.parent_id ? `Delete ${taskObj.title}` : 'Delete task tree'}
          onClick={(e) => {
            e.stopPropagation();
            if (!taskObj.parent_id) {
              if (onRootDelete) onRootDelete(taskObj);
              return;
            }
            if (window.confirm(`Are you sure you want to delete the task "${taskObj.title}"?`)) {
              deleteSubtask(taskObj.parent_id, taskObj.id);
            }
          }}
          sx={{ p: 0.5 }}
        >
          <Delete color="secondary" />
        </IconButton>
      ),
    };

    return (
       <div
        >
      {isDropTarget && dragState.position === 'before' && (
        <Box sx={dropIndicatorSx} onDragOver={(event) => event.preventDefault()} onDrop={(event) => handleDrop(event, taskObj.id)}>
          DROP HERE
        </Box>
      )}
      <TreeItem
        itemId={taskObj.id}
        key={taskObj.id}
        sx={{
          // MUI clips labels by default; the neon card needs to glow past its edge.
          '& .MuiTreeItem-content': { overflow: 'visible' },
          '& .MuiTreeItem-label': { width: '100%', overflow: 'visible' },
          '& .MuiTreeItem-groupTransition': { overflow: 'visible' },
        }}
        label={
          // ⬇️ your block goes right here (no changes needed)
          <Box
            id={`${idPrefix}-task-${taskObj.id}`}
            draggable={!taskObj.parent_id ? false : true}
            onDragStart={(event) => handleDragStart(event, taskObj.id)}
            onDragOver={(event) => handleDragOver(event, taskObj.id)}
            onDrop={(event) => handleDrop(event, taskObj.id)}
            onDragEnd={resetDragState}
          >
            <Box
              id={`${idPrefix}-add-step-${taskObj.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                border: '2px solid',
                borderColor: isDropTarget && dragState.position === 'inside' ? 'secondary.main' : synthBlue,
                borderRadius: 4,
                p: 1.25,
                width: '100%',
                minWidth: 0,
                cursor: taskObj.parent_id ? 'grab' : 'default',
                opacity: isDragged ? 0.35 : 1,
                // The page theme supplies the neon accents; this local surface is
                // deliberately near-black to keep the Synth Brawler contrast.
                bgcolor: '#0A0F0D',
                boxShadow: isDropTarget && dragState.position === 'inside'
                  ? '0 0 18px rgba(255, 45, 149, 0.65)'
                  : '0 4px 15px rgba(255, 45, 149, 0.5)',
                transition: 'opacity 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                transform: isDropTarget && dragState.position === 'inside' ? 'scale(1.015)' : 'none',
                '&:active': { cursor: taskObj.parent_id ? 'grabbing' : 'default' },
                '&:hover': { boxShadow: '0 4px 25px rgba(255, 45, 149, 0.8)' },
              }}
            >
              {/* Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <DragIndicator sx={{ color: synthBlue, opacity: taskObj.parent_id ? 0.7 : 0.25, flexShrink: 0 }} />
                <Typography
                  variant={"body2"}
                  sx={{
                  overflow: 'auto',
                  textAlign: 'left',
                  color: synthBlue,
                }}
                >
                {taskObj.title}
                </Typography>
              </Box>

              {/* Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexShrink: 0,
                  justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                {renderIconButtons
                  ? renderIconButtons({
                    task: taskObj,
                    addSubtask,
                    openDuplicateModal,
                    handleSelect,
                    deleteSubtask,
                    defaultButtons,
                    location: 'task',
                  })
                  : (
                    <>
                      {defaultButtons.edit}
                      {defaultButtons.delete}
                    </>
                  )}
              </Box>
            </Box>
          </Box>
        }
      >
        {isDropTarget && dragState.position === 'inside' && (
          <Box sx={dropIndicatorSx} onDragOver={(event) => event.preventDefault()} onDrop={(event) => handleDrop(event, taskObj.id)}>
            NEST INSIDE
          </Box>
        )}
        {/* recurse children if you have them */}
        {sortedSubtasks.map((st) => renderRecursiveTasks(st))}
        <TreeItem
          itemId={`add-breakdown-${taskObj.id}`}
          sx={{
            '& .MuiTreeItem-content': {
              overflow: 'visible',
              cursor: 'default',
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': { bgcolor: 'transparent' },
              '&.Mui-selected:hover': { bgcolor: 'transparent' },
            },
            '& .MuiTreeItem-label': { width: '100%', overflow: 'visible' },
          }}
          label={
            <Box
              aria-haspopup="dialog"
              onClick={(event) => {
                event.stopPropagation();
                setBreakdownTarget(taskObj.id);
              }}
              sx={{ ...breakdownRowSx, cursor: 'pointer' }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: 1.2,
                  textAlign: 'left',
                }}
              >
                Add step to {taskObj.title || 'TASK'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {defaultButtons.add}
                {defaultButtons.duplicate}
              </Box>
            </Box>
          }
        />
      </TreeItem>
      {isDropTarget && dragState.position === 'after' && (
        <Box sx={dropIndicatorSx} onDragOver={(event) => event.preventDefault()} onDrop={(event) => handleDrop(event, taskObj.id)}>
          DROP HERE
        </Box>
      )}
      </div>
    );
  };

  const DuplicateTaskModal = () => {
    if (!showDuplicateModal) return null;
    return (
      <TaskSelectionDialog
        idPrefix={`${idPrefix}-duplicate`}
        open={showDuplicateModal}
        onClose={closeDuplicateModal}
        onConfirm={duplicateSelectedTask}
        tasks={currentTasks}
        title="Select Task to Copy"
        confirmLabel="Duplicate"
        error={duplicateError || error}
        isLoading={isLoading}
      />
    );
  };

  const BreakdownChoiceDialog = () => {
    const parentTask = breakdownTarget ? task.getTask(breakdownTarget) : null;
    const parentName = parentTask?.title || 'task';

    return (
      <Dialog id={`${idPrefix}-breakdown-dialog`} open={!!breakdownTarget} onClose={closeBreakdownDialog}>
        <DialogTitle>Add step to {parentName}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 4,
              pt: 1,
              minWidth: { sm: 280 },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                id={`${idPrefix}-breakdown-manual`}
                aria-label={`Manually add a subtask to ${parentName}`}
                onClick={addManualBreakdown}
                sx={{ p: 1.5 }}
              >
                <AddBox color="green" />
              </IconButton>
              <Typography variant="body2">Manual</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <IconButton
                id={`${idPrefix}-breakdown-duplicate`}
                aria-label={`Duplicate a subtask into ${parentName}`}
                onClick={duplicateBreakdown}
                sx={{ p: 1.5 }}
              >
                <ContentCopy color="orange" />
              </IconButton>
              <Typography variant="body2">Duplicate</Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
    <Box id={`${idPrefix}-form`} sx={{ width: 'min(1200px, calc(100vw - 16px))', mx: 'auto' }}>
      <SimpleTreeView
        id={`${idPrefix}-tree`}
        expandedItems={expandedItems}
        onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
        onItemSelectionToggle={handleItemSelectionToggle}
        defaultCollapseIcon={<ExpandMore />}
        defaultExpandIcon={<ChevronRight />}
      >
        {renderRecursiveTasks(task)}
      </SimpleTreeView>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
        <Button id={`${idPrefix}-save`} onClick={handleSubmit} disabled={!!initialTask && isSubmitting}>
          {!!initialTask && isSubmitting ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 28,
                overflow: 'hidden',
                '& > *': {
                  transform: 'scale(0.28)',
                  transformOrigin: 'center',
                },
              }}
            >
              <LoadingIndicator text="updating" />
            </Box>
          ) : (
            submitButtonText
          )}
        </Button>
      </Box>
    </Box>
    <BreakdownChoiceDialog />
    <DuplicateTaskModal />
    <Dialog id={`${idPrefix}-task-dialog`} open={!!selectedTask} onClose={handleClose}>
  <DialogTitle>{dialogMode === 'edit' ? 'Edit Task' : 'Add Task'}</DialogTitle>
  <DialogContent>
    <TaskInputForm idPrefix={`${idPrefix}-task-input`} task={selectedTask} buttonText={dialogMode === 'edit' ? 'Update' : 'Add'} buttonClick={handleTaskUpdate} handleClose={handleClose} fields={inputFields} />
  </DialogContent>
</Dialog>
</>
  );
};

export default TaskForm;
